import { Anchor, Box, Collapsible, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import type { RiskModel } from '@zg-rentals/ts-capps-client';
import { getScoreFactorDescription, REASON_CODE_URL } from '@zg-rentals/applications-utils';

export const CreditReportScoreFactors = ({ riskModel }: { riskModel: RiskModel }) => {
  return (
    <Box css={{ marginTop: 'sm', marginBottom: 'sm' }}>
      <Collapsible
        defaultCollapsed
        // eslint-disable-next-line react/no-children-prop
        children={(collapsed: boolean, toggle: () => void) => (
          <>
            {!collapsed && (
              <Box css={{ marginTop: 'sm', marginBottom: 'sm' }}>
                <Heading level={4} textStyle="body-lg-bold" css={{ marginBottom: 'xs' }}>
                  Credit score factors
                </Heading>
                <Paragraph textStyle="body-sm">
                  The factors are listed in order of importance based on their influence on the credit score. For an
                  easy-to-understand explanation of each code, visit{' '}
                  <Anchor href={REASON_CODE_URL} target="_blank">
                    reasoncode.org
                  </Anchor>
                  .
                </Paragraph>
                <RiskModelList riskModel={riskModel} isPositive={false} />
                <RiskModelList riskModel={riskModel} isPositive />
              </Box>
            )}

            <Box css={{ display: 'flex', justifyContent: 'center' }}>
              <TextButton
                textStyle="body-sm"
                onClick={toggle}
                icon={collapsed ? <IconChevronDownFilled /> : <IconChevronUpFilled />}
              >
                {`${collapsed ? 'See' : 'Hide'} credit score factors`}
              </TextButton>
            </Box>
          </>
        )}
      />
    </Box>
  );
};

const SCORE_FACTOR_CODES = [
  'ScoreFactorCodeOne',
  'ScoreFactorCodeTwo',
  'ScoreFactorCodeThree',
  'ScoreFactorCodeFour',
  'ScoreFactorCodeFive',
];

const RiskModelList = ({ riskModel, isPositive }: { riskModel: RiskModel; isPositive: boolean }) => {
  const scoreFactorCodes = isPositive
    ? SCORE_FACTOR_CODES.map((scoreFactorCode) => `positive${scoreFactorCode}`)
    : SCORE_FACTOR_CODES;

  const shouldRender =
    riskModel &&
    scoreFactorCodes.filter((_, index) => !!riskModel[scoreFactorCodes[index] as keyof RiskModel]).length > 0;

  return (
    <Box css={{ marginTop: 'xs' }}>
      <Text textStyle="body-sm-bold">{isPositive ? 'Positive' : 'Negative'} score factors</Text>
      {shouldRender ? (
        <>
          {scoreFactorCodes
            .filter((scoreFactorCode) => !!riskModel[scoreFactorCode as keyof RiskModel])
            .map((scoreFactorCode, index) => {
              const scoreFactor = riskModel[scoreFactorCode as keyof RiskModel] as string;
              const scoreDescription = getScoreFactorDescription(riskModel, scoreFactor, scoreFactor[0] === 'P');
              return (
                <Paragraph textStyle="body-sm" key={index}>
                  {String(riskModel[scoreFactorCode as keyof RiskModel])}: {scoreDescription}
                </Paragraph>
              );
            })}
        </>
      ) : (
        <Text textStyle="body-sm-bold">{isPositive ? 'Positive' : 'Negative'} score factors</Text>
      )}
    </Box>
  );
};
